import { commonMessages } from '@/__scripts/common.messages';
import { T } from '@/components/i18n/message.i18n.component';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion/accordion.component';
import Button from '@/components/ui/button/button.component';
import { DesignObligation, MaturityStage, ProductBuildingBlock } from '@/gql/graphql';
import { GroupedBuildingBlock, SortedBuildingBlock } from '@/hooks/use-building-blocks/use-building-blocks.hook.core';
import { pretailoringPageMessages } from '@/pages/products/pre-tailoring/pre-tailoring.messages';
import { tailoringMessages } from '@/pages/products/tailoring/tailoring.messages';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { NoResults } from '../../no-results/no-results.component';
import { SingleBuildingBlock } from '../single-building-block/single-building-block.component';

type BuildingBlockListProps = {
  sort: 'DO' | 'MTS';
  context: 'included' | 'excluded';
  buildingBlocks: SortedBuildingBlock<DesignObligation | MaturityStage>;
  selectedBuildingBlockId?: string;
  enableInclusion?: boolean;
  onBBInclusionChange?: (groupedBB: GroupedBuildingBlock<DesignObligation>, including: boolean) => void;
  handleBlockClick?: (bb: ProductBuildingBlock) => void;
  height?: number;
};

export const BuildingBlockList = ({
  sort,
  context,
  buildingBlocks,
  enableInclusion,
  selectedBuildingBlockId,
  handleBlockClick,
  onBBInclusionChange,
  height,
}: BuildingBlockListProps) => {
  const t = useIntl();
  const location = useLocation();
  const isTailoring = !location.pathname.includes('pre-tailoring');
  const [accordionOpen, setAccordionOpen] = useState<string>();
  return (
    <div className="flex flex-col">
      {buildingBlocks.data.length === 0 && (
        <NoResults
          className="flex justify-center items-center"
          style={{ height: height ? `${height}px` : 'auto' }}
          data-testid={`building-blocks-${context}-bb-no-results`}
          message={<T {...pretailoringPageMessages.noBuildingBlocks} />}
        />
      )}
      <Accordion
        onValueChange={(value) => {
          setAccordionOpen(value);
        }}
        value={accordionOpen}
        type="single"
        collapsible
        className="w-full flex flex-col gap-y-2"
      >
        {buildingBlocks.data.map((item: GroupedBuildingBlock<DesignObligation | MaturityStage>) => {
          return (
            <AccordionItem
              data-testid={`building-blocks-${context}-accordion-item`}
              key={item.id}
              value={item.id || ''}
              className="border rounded border-other-stroke p-4"
            >
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-col gap-y-1">
                  <Button
                    variant="ghost"
                    data-testid={`building-blocks-${context}-group-title-accordion-trigger-${item.id}`}
                    onClick={() => {
                      if (accordionOpen === item.id) setAccordionOpen('');
                      if (accordionOpen !== item.id) setAccordionOpen(item.id);
                    }}
                  >
                    <p data-testid={`building-blocks-${context}-group-title-${item.id}`} className="typo-primary-500">
                      {item.name}
                    </p>
                  </Button>
                  <p
                    data-testid={`building-blocks-${context}-group-bb-count-${item.id}`}
                    className="typo-secondary text-secondary-text"
                  >
                    {t.formatMessage(commonMessages.buildingBlocks, {
                      number: item.buildingBlocks.length,
                    })}
                  </p>
                </div>
                <div className="flex flex-row justify-between items-center gap-x-6">
                  {isTailoring && sort === 'DO' && enableInclusion && (
                    <Button
                      variant="ghost"
                      className="items-start justify-start px-0"
                      data-testid={`building-blocks-${context}-group-include-exclude-button`}
                      onClick={() => {
                        onBBInclusionChange &&
                          onBBInclusionChange(item as GroupedBuildingBlock<DesignObligation>, context !== 'included');
                      }}
                    >
                      {context === 'excluded' ? (
                        <T {...tailoringMessages.include} />
                      ) : (
                        <T {...tailoringMessages.exclude} />
                      )}
                    </Button>
                  )}
                  <AccordionTrigger
                    onClick={() => setAccordionOpen(item.id)}
                    data-testid={`building-blocks-${context}-group-accordion-trigger-${item.id}`}
                  />
                </div>
              </div>
              <AccordionContent data-testid={`building-blocks-${context}-group-accordion-content`}>
                <div className="flex flex-col gap-y-3 mt-2">
                  {item.buildingBlocks.map((bb) => {
                    return (
                      <SingleBuildingBlock
                        key={bb.id}
                        sortedBy={sort}
                        context={context}
                        buildingBlock={bb}
                        onBlockClick={handleBlockClick}
                        selected={selectedBuildingBlockId === bb.id}
                      />
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};
